import React, {
  useCallback, useState, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import ReactModal from 'react-modal';
import classnames from 'classnames';
import { useLocation } from 'react-router-dom';

import { ReactComponent as ChevronLeftIcon } from 'assets/svg/chevron-left.svg';

import container from 'containers/HeaderMenuContainer';

import HeaderMenuList from 'components/headerMenu/HeaderMenuList';

import { matchRoute } from 'utils/routeHelpers';
import { isDesktopScreen } from 'utils/screenHelper';
import {
  CONFIRM_DELETION,
  DELETE_ACCOUNT,
  MY_ACCOUNT_PREFIX,
} from 'utils/routes';
import siteStyle from 'utils/siteStyle';

export const HeaderMenuCustom = ({
  open,
  toggleHeaderMenu,
  onBackTo,
  startEl,
  endEl,
}) => {
  const path = useLocation().pathname;
  const [isMenuOpen, setIsMenuOpen] = useState(open);
  const [extraClassMiddle, setExtraClassMiddle] = useState('');
  const [extraClassTop, setExtraClassTop] = useState('');
  const [extraClassBottom, setExtraClassBottom] = useState('');
  const [extraClassHeader, setExtraClassHeader] = useState('');
  const [extraClassMenu, setExtraClassMenu] = useState('max-h-0 ease-out');
  const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

  const updateDimensions = useCallback(() => {
    if (isDesktopScreen()) {
      toggleHeaderMenu();
    }
  }, [toggleHeaderMenu]);

  useEffect(() => {
    if (!open) {
      setExtraClassMiddle('');
      setExtraClassTop('');
      setExtraClassBottom('');
      setExtraClassMenu('max-h-0 ease-out');
      setExtraClassHeader('');
      window.removeEventListener('resize', updateDimensions);
    } else {
      setExtraClassHeader('fixed');
      window.addEventListener('resize', updateDimensions);
    }
    return () => window.removeEventListener('resize', updateDimensions);
  }, [
    open, setExtraClassTop, setExtraClassMiddle, setExtraClassBottom,
    setExtraClassMenu, setExtraClassHeader, updateDimensions,
  ]);

  const toggleMenu = useCallback(() => {
    if (!open) {
      setExtraClassMiddle('opacity-0');
      setExtraClassTop('-rotate-45 translate-x-[-5px] translate-y-[7.3px]');
      setExtraClassBottom('rotate-45 translate-x-[-5px] translate-y-[-7.3px]');
      setIsMenuOpen(true);
      setExtraClassMenu('max-h-screen ease-in');
      sleep(500).then(() => toggleHeaderMenu());
    } else {
      sleep(500).then(() => setIsMenuOpen(false));
      toggleHeaderMenu();
    }
  }, [toggleHeaderMenu, setExtraClassTop, setExtraClassBottom, setExtraClassMenu, open]);

  const navbar = (
    <div
      className={classnames('w-full h-16 flex items-center py-3 pl-6 top-0 z-50 pr-4', siteStyle({ gb: 'bg-secondary', rg: 'bg-primary', default: 'bg-white' }), extraClassHeader, {
        'md:fixed': path.includes(MY_ACCOUNT_PREFIX) && !(matchRoute(DELETE_ACCOUNT, path) || matchRoute(CONFIRM_DELETION, path)),
        'border-b border-grey-4 md:border-none': !isMenuOpen,
      })}
    >
      <button type="button" onClick={toggleMenu} className="flex flex-col justify-between w-5 h-4 focus:outline-none" aria-label="Expand Menu">
        <div className={classnames('w-5 h-0.5 transition-all duration-500 my-0 rounded', siteStyle({ gb: 'bg-white', rg: 'bg-white', default: 'bg-dark' }), extraClassTop)} />
        <div className={classnames('w-5 h-0.5 transition-all duration-500 my-0 rounded', siteStyle({ gb: 'bg-white', rg: 'bg-white', default: 'bg-dark' }), extraClassMiddle)} />
        <div className={classnames('w-5 h-0.5 transition-all duration-500 my-0 rounded', siteStyle({ gb: 'bg-white', rg: 'bg-white', default: 'bg-dark' }), extraClassBottom)} />
      </button>
      {!isMenuOpen && (
        <div className="flex items-center w-full">
          {(startEl || onBackTo) && <div className="w-px h-5 ml-3 mr-1 bg-light-grey-four" />}
          <div className="h-9 flex items-center justify-between w-full">
            <div>
              {onBackTo && (
                <button
                  className="h-9 w-9 flex items-center justify-center border-none focus:outline-none"
                  type="button"
                  onClick={onBackTo}
                >
                  <ChevronLeftIcon className={siteStyle({
                    common: 'fill-current',
                    gb: 'text-white',
                    rg: 'text-white',
                    default: 'text-dark',
                  })}
                  />
                </button>
              )}
            </div>
            {startEl}
            <div>
              {endEl}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div>
      {navbar}
      <div className={classnames('z-40 fixed top-16 bg-white w-full text-4.5 overflow-auto transition-max-height duration-500', extraClassMenu)}>
        {!open && <HeaderMenuList toggleMenu={toggleMenu} />}
        <ReactModal
          isOpen={open}
          className="fixed inset-0 flex flex-col bg-white outline-none text-4.5 top-16 font-primary"
          style={{ overlay: { zIndex: 100 } }}
          ariaHideApp={false}
          bodyOpenClassName="overflow-hidden"
        >
          {navbar}
          <HeaderMenuList toggleMenu={toggleMenu} />
        </ReactModal>
      </div>
    </div>
  );
};

HeaderMenuCustom.defaultProps = {
  onBackTo: null,
  startEl: null,
  endEl: null,
};

HeaderMenuCustom.propTypes = {
  location: PropTypes.shape({
    city: PropTypes.string,
    state: PropTypes.string,
    lat: PropTypes.number,
    lon: PropTypes.number,
    hierarchizedUrl: PropTypes.string,
  }).isRequired,
  open: PropTypes.bool.isRequired,
  toggleHeaderMenu: PropTypes.func.isRequired,
  router: PropTypes.shape({
    location: PropTypes.shape({
      pathname: PropTypes.string,
      search: PropTypes.string,
      query: PropTypes.shape({
        date: PropTypes.string,
      }),
    }),
  }).isRequired,
  city: PropTypes.shape({
    isLoading: PropTypes.bool,
    city: PropTypes.shape({
      id: PropTypes.number,
      fullName: PropTypes.string,
    }),
  }).isRequired,
  course: PropTypes.shape({
    name: PropTypes.string,
  }).isRequired,
  onBackTo: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  startEl: PropTypes.node,
  endEl: PropTypes.node,
};

export default container(HeaderMenuCustom);
